/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

// @mui material components
import React, { useState } from 'react'

import { Dialog } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { FirebaseError } from 'firebase/app'
import { updatePassword } from 'firebase/auth'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context'

function ChangePassword(): JSX.Element {
	const [controller, _] = useMaterialUIController()
	const [password, setPassword] = useState<string>('')
	const [confirmPassword, setConfirmPassword] = useState<string>('')
	const [dialogOpen, setDialogOpen] = useState<boolean>(false)
	const [dialogMsg, setDialogMsg] = useState<{ header: string; content: string }>()
	const { session } = controller
	const passwordRequirements = ['Min 6 characters', 'One number (2 are recommended)']

	const handleChangePasswordClick = (text: string) => {
		// Check if password is at least 6 characters
		const minLengthValid = text.length >= 6

		// Check if password contains at least one number (2 recommended)
		const containsNumber = /\d/.test(text) // Regex to check if text has a number
		const hasAtLeastOneNumber = containsNumber

		// If both conditions are met
		if (password === confirmPassword && minLengthValid && hasAtLeastOneNumber) {
			updatePassword(session.user, text)
				.then(() => {
					setDialogMsg({
						header: 'Password Updated Successfully ✅',
						content: 'Thank you, please continue as normal.',
					})
					setDialogOpen(true)
					setPassword('')
					setConfirmPassword('')
				})
				.catch((error: FirebaseError) => {
					console.log('Error Updating Password ❌', error)
					if (error.code === 'auth/requires-recent-login') {
						setDialogMsg({
							header: 'Please Re-Sign In to Update Password',
							content:
								'You need to be recently signed in to update your password, please sign out and back in again to update.',
						})
						setDialogOpen(true)
					} else {
						setDialogMsg({
							header: 'Error Updating Password',
							content: 'There was an error updating your password.',
						})
						setDialogOpen(true)
					}
				})
		} else {
			setDialogMsg({
				header: 'Error Updating Password ❌',
				content:
					'The passwords must match, have a minimum of 6 characters and at least one number.',
			})
			setDialogOpen(true)
		}
	}

	const renderPasswordRequirements = passwordRequirements.map((item, key) => {
		const itemKey = `element-${key}`

		return (
			<MDBox key={itemKey} component="li" color="text" fontSize="1.25rem" lineHeight={1}>
				<MDTypography variant="button" color="text" fontWeight="regular" verticalAlign="middle">
					{item}
				</MDTypography>
			</MDBox>
		)
	})

	return (
		<Card id="change-password">
			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
				<MDBox width={500} height={150} style={{ backgroundColor: '#fff' }}>
					<MDBox
						p={3}
						display={'flex'}
						flexDirection={'column'}
						justifyContent={'center'}
						alignItems={'center'}
						width={'100%'}
						gap={1}
					>
						<MDTypography variant="h4">{dialogMsg?.header}</MDTypography>
						<MDTypography variant="body2" textAlign="center">
							{dialogMsg?.content}
						</MDTypography>
					</MDBox>
				</MDBox>
			</Dialog>
			<MDBox p={3}>
				<MDTypography variant="h5">Change Password</MDTypography>
			</MDBox>
			<MDBox component="form" pb={3} px={3}>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<MDInput
							fullWidth
							label="New Password"
							value={password}
							inputProps={{ type: 'password', autoComplete: '' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12}>
						<MDInput
							fullWidth
							label="Confirm New Password"
							value={confirmPassword}
							inputProps={{ type: 'password', autoComplete: '' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setConfirmPassword(e.target.value)
							}
						/>
					</Grid>
				</Grid>
				<MDBox mt={6} mb={1}>
					<MDTypography variant="h5">Password requirements</MDTypography>
				</MDBox>
				<MDBox mb={1}>
					<MDTypography variant="body2" color="text">
						Please follow this guide for a strong password
					</MDTypography>
				</MDBox>
				<MDBox display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap">
					<MDBox component="ul" m={0} pl={3.25} mb={{ xs: 8, sm: 0 }}>
						{renderPasswordRequirements}
					</MDBox>
					<MDBox ml="auto">
						<MDButton
							variant="gradient"
							color="secondary"
							size="small"
							onClick={() => {
								handleChangePasswordClick(password)
							}}
						>
							update password
						</MDButton>
					</MDBox>
				</MDBox>
			</MDBox>
		</Card>
	)
}

export default ChangePassword
